import { betAccountQueryOptions } from '@mobi/account/Hooks/useBetAccount'
import { queryKeys as accountQueryKeys } from '@mobi/account/Utils'
import type { BetAccount } from '@mobi/api-types'
import { AppRoutes } from '@core/AppRoutes'
import { toggleIsDropDownOpen } from '@core/Areas/AppHeader/Store'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { queryClient } from '@core/Data/ReactQuery/queryClient'
import { syncLoginStateWithNativeApp } from '@core/NativeServices/LoginStateRegister/LoginStateRegister'
import { updateMetadata } from '@core/NativeServices/MetadataService'
import { updateAccountNumberOnDataLayer } from '@core/Services/Analytics/helpers/dataLayer/updaters'
import { setFeatures } from '@core/State/FeatureFlags/Store'
import { getFeaturesIsUserAnonymous } from '@core/State/FeatureFlags/Store/selectors'
import { state$ as navigationState$ } from '@core/State/Navigation/driver'
import { store } from '@core/Store'
import { startListening } from '@core/Store/listenerMiddleware'
import { handleAutoRegisterFcmToken } from '@core/Utils/notifications/pushNotificationAlerts'
import { appHistory } from '@core/Utils/routing/history'
import { fetchAccountBalanceAsync, fetchCampaignsAsync } from './async-signals'
import { register as registerUserAccountPushData } from './push-data'
import { SetIsShareMyBetBlocked, SetResidentialDetail, state$ } from './userAccountDriver'

export function registerUserAccountSideEffects(): void {
  registerUserAccountPushData()

  startListening({
    actionCreator: setFeatures,
    effect: (_, listenerApi) => {
      const currentIsUserAnonymous = getFeaturesIsUserAnonymous(listenerApi.getState())
      const hasLoggedInAndFetchedFlags = currentIsUserAnonymous === false
      if (hasLoggedInAndFetchedFlags) fetchCampaignsAsync()
    },
  })

  // Handle Side Effects Based on State Changes
  state$
    .pairwise()
    .distinctUntilChanged()
    .subscribe(([prevState, newState]) => {
      const hasLoggedOut = prevState.isLoggedIn === true && newState.isLoggedIn === false
      const hasLoggedIn = !prevState.isLoggedIn && newState.isLoggedIn === true

      if (hasLoggedIn && newState.accountNumber) {
        fetchAccountBalanceAsync()
        updateMetadata()
        updateAccountNumberOnDataLayer(`${newState.accountNumber || ''}`)
        handleAutoRegisterFcmToken(newState.accountNumber)

        queryClient.fetchQuery(betAccountQueryOptions).then((betAccount: BetAccount) => {
          syncLoginStateWithNativeApp(betAccount)
          const { ResidentialCountry, ResidentialState } = betAccount
          SetResidentialDetail([ResidentialCountry, ResidentialState])
          SetIsShareMyBetBlocked({ isShareMyBetBlocked: betAccount.BlockShareMyBetInd === 'Y' })
        })
      }

      if (hasLoggedOut) {
        updateAccountNumberOnDataLayer(null)
        queryClient.removeQueries({ queryKey: [queryKeys.accountBase] })
        queryClient.removeQueries({ queryKey: [accountQueryKeys.sessionScoped] })
        store.dispatch(toggleIsDropDownOpen(false))

        navigationState$.take(1).subscribe(navState => {
          if (navState.get('currentUrl')?.includes('account')) {
            appHistory.push(AppRoutes.Home)
          }
        })
      }
    })
}
